/**
 * The Properties list, and the form that adds one.
 *
 * **The overview moved out of this file** to `DevelopmentOverview.tsx` when the rail was cut to
 * eight items. It had grown from "who is behind" into the screen every figure in the module links
 * back to, and a dashboard sharing a file with the list it links to is a file two people edit for
 * two different reasons.
 *
 * **A new Property assumes homes.** The operator: *"Mostly houses for clients to purchase. But I'd
 * want them to be able to switch."* The `sells` picker had no favourite, so the commonest case cost
 * the most presses. Switching is one control and stays obvious — an agency selling land is not a
 * rare case, it is the second commonest, and a default is a suggestion rather than a decision.
 * Nothing else changes: `soldNoun()` still decides every word from the Property's own setting, so a
 * land scheme still says "plot" everywhere.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useApiMutation, formValues } from "@/lib/mutations";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LinkButton,
  PageHeader,
  Select,
  Spinner,
  Table,
} from "@/components/ui";
import { SearchBox } from "@/components/list-toolbar";
import { SELLS, SELLS_INFO, soldNoun } from "@rentorng/core";
import { PhotoThumbnail } from "@/components/gallery";
import { useListParams } from "@/lib/list-params";
import { useStickySort } from "@/lib/sort";
import { usePageTitle } from "@/lib/title";
import { Refusal } from "@/components/padlock";
import { BLANK_TERMS, ReservationTermsFields, estateTermsPayload, type TermsDraft } from "./Reservations";

/* ------------------------------------------------------------------ the list */

interface EstateRow {
  id: string;
  name: string;
  status: string;
  city: string | null;
  state: string | null;
  titleType: string | null;
  sells: string;
  plots: number;
  available: number;
  sold: number;
  /** The photograph the row leads with, or null. Counted by the server in the same statement. */
  leadPhotoId: string | null;
  /** How many things inside it a stranger can see. Zero means the scheme is not public at all. */
  listedCount: number;
}

const STATUS_TONE: Record<string, "brand" | "success" | "warn" | "muted"> = {
  planning: "muted",
  selling: "success",
  sold_out: "brand",
  closed: "warn",
};

/**
 * How this list can be ordered.
 *
 * **Newest first is the default**, because the commonest reason to open this page is that
 * something has just been added to it. The choice is remembered — see `useStickySort` — so
 * somebody who works alphabetically finds it alphabetical tomorrow.
 */
const SORTS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "name", label: "Name (A–Z)" },
  { value: "plots", label: "Most units" },
  { value: "available", label: "Most available" },
  { value: "sold", label: "Most sold" },
];

export function DevelopmentPropertiesPage() {
  const list = useListParams();
  const sort = useStickySort("estates", SORTS);
  const [params, setParams] = useSearchParams();

  /*
    The heading says "Properties" and so does the letting list's, which is correct on both screens
    and wrong in a tab. An agency with both modules keeps them open side by side, and two tabs
    reading "Properties · RentorNG" are two tabs nobody can tell apart — the same failure
    `verify-titles.mjs` exists to catch, on the one pair of screens it does not yet visit.

    So the tab says what is being sold and the heading does not repeat it. Called after
    `PageHeader` renders rather than instead of it: a child's effect runs before its parent's, so
    the header sets "Properties" and this overwrites it, which is the order that gives a screen
    with a heading a title for free and lets one that needs its own say so.
  */
  usePageTitle("Properties for sale");

  /*
    "Just added" — named, and dismissible.

    The form used to land on a confirmation screen of its own, which is a whole page spent saying
    something the list itself can say while also showing the result. The id comes back in the
    address so the row can be pointed at; the message clears from the address when it is dismissed,
    so a refresh does not show it again.
  */
  const created = params.get("created");
  const dismissCreated = () => {
    const next = new URLSearchParams(params);
    next.delete("created");
    setParams(next, { replace: true });
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["estates", list.q, list.page, sort.sort],
    queryFn: () =>
      api.get<{ estates: EstateRow[]; page: { total: number; pages: number; page: number } }>(
        `/estates?q=${encodeURIComponent(list.q ?? "")}&page=${list.page}&sort=${sort.sort}`,
      ),
  });

  const justAdded = created ? data?.estates.find((estate) => estate.id === created) : undefined;

  return (
    <div>
      <PageHeader
        title="Properties"
        subtitle={`${data?.page?.total ?? 0} being sold`}
        description="Every property on sale. Open one to see its contents, the buyers and the remaining availability."
        actions={<LinkButton to="/app/development/properties/new" need="property.edit">Add a property</LinkButton>}
      />

      {created ? (
        <div className="mb-4">
          <Alert tone="success" title={justAdded ? `${justAdded.name} was added.` : "The property was added."}>
            It appears at the top of the list. Open it to add its units — a numbered range can be created in one step.{" "}
            <button type="button" className="font-semibold underline" onClick={dismissCreated}>
              Dismiss
            </button>
          </Alert>
        </div>
      ) : null}

      <Card padded={false}>
        <div className="flex flex-wrap items-center gap-2 border-b border-ink-200 p-3">
          <div className="min-w-[16rem] flex-1">
            <SearchBox params={list} placeholder="Search properties by name or area…" />
          </div>
          <Select
            aria-label="Sort properties"
            className="w-48"
            value={sort.sort}
            onChange={(event) => sort.setSort(event.target.value)}
          >
            {sort.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        {isLoading ? (
          <Spinner />
        ) : error ? (
          <ErrorState error={error} retry={() => refetch()} />
        ) : !data?.estates.length ? (
          <EmptyState
            title={list.q ? `No properties matched “${list.q}”.` : "No properties recorded."}
            hint="A property is the scheme being sold. Add the property first, then its contents — a numbered range can be created in one step."
            action={<LinkButton to="/app/development/properties/new" need="property.edit">Add a property</LinkButton>}
          />
        ) : (
          <Table
            head={
              <tr>
                <th>Name</th>
                <th>Location</th>
                <th>Title</th>
                <th>Listing</th>
                <th className="num">For sale</th>
                <th className="num">Available</th>
                <th className="num">Sold</th>
                <th>Status</th>
              </tr>
            }
          >
            {data.estates.map((estate) => (
              /* The new row says which it is, rather than leaving somebody to work out that the
                 top one is theirs — which is only true under one of the six sorts. */
              <tr key={estate.id} className={estate.id === created ? "bg-success-50" : undefined}>
                <td>
                  <span className="flex items-center gap-3">
                    {/* The leading image of the scheme. A list of schemes with no pictures is a
                        list somebody has to read rather than recognise. */}
                    <PhotoThumbnail photoId={estate.leadPhotoId} alt="" className="size-11" />
                    <Link className="font-medium text-brand-700 hover:underline" to={`/app/development/properties/${estate.id}`}>
                      {estate.name}
                    </Link>
                  </span>
                </td>
                <td className="text-ink-600">{[estate.city, estate.state].filter(Boolean).join(", ") || "—"}</td>
                <td className="text-ink-600">{estate.titleType ?? "—"}</td>
                <td>
                  {/* Whether a stranger can see any of it. The estate has no switch of its own —
                      it is public exactly when something inside it is — so this is counted rather
                      than stored, and it is the honest answer to "is this scheme live". */}
                  {estate.listedCount > 0 ? (
                    <Badge tone="success">{estate.listedCount} public</Badge>
                  ) : (
                    <span className="text-xs text-ink-400">Private</span>
                  )}
                </td>
                <td className="num">
                  {estate.plots}
                  {/* The word this scheme uses, on its own row — a list of properties is very often
                      a land scheme beside a terrace of houses. */}
                  <div className="text-xs text-ink-400">{soldNoun(estate.sells).many}</div>
                </td>
                <td className="num">{estate.available}</td>
                <td className="num">{estate.sold}</td>
                <td>
                  <Badge tone={STATUS_TONE[estate.status] ?? "muted"}>{estate.status.replace("_", " ")}</Badge>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------------ the form */

/**
 * What a new Property is assumed to be selling.
 *
 * **Homes**, on the operator's instruction — *"Mostly houses for clients to purchase"* — where the
 * picker previously had no favourite and opened on "mixed", the one answer that is right for
 * nobody. It is a suggestion and not a decision: the control sits in the ordinary flow of the form,
 * one press changes it, and an existing Property is untouched by this.
 */
const DEFAULT_SELLS = "homes";

export function DevelopmentPropertyForm() {
  const navigate = useNavigate();
  /*
    Controlled, because the rest of the form has to know the answer.

    What a scheme sells decides what everything inside it is called, and the reservation switches
    below say "takes the plot off the market" or "takes the unit off the market" accordingly. A
    developer who has just told the form they are selling land should not then be asked about units
    two fields later.
  */
  const [sells, setSells] = useState<string>(DEFAULT_SELLS);
  /*
    How this property reserves — **defaults, not terms.**

    Every reservation copies all five at the moment it is taken and then owns its own answers, so
    changing these later moves what the next buyer is offered and touches nothing already held. They
    are folded away because the ordinary property is created without anybody thinking about
    reserving at all, and five controls in the way of that would make the common case the harder one.
  */
  const [terms, setTerms] = useState<TermsDraft>(BLANK_TERMS);
  const [reserving, setReserving] = useState(false);
  const save = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post<{ id: string }>("/estates", input),
    invalidates: [["estates"], ["developer-summary"]],
    /* Straight to the list, with the property on it.
       A confirmation screen spends a whole page saying something the list can say while also
       showing the result — and it left somebody one more click from the thing they came to do. */
    onSuccess: (result) => navigate(`/app/development/properties?created=${result.id}`, { replace: true }),
  });

  return (
    <div className="max-w-2xl">
      {/*
        The back link is the list, not `/app/development/properties/all`.

        The `/all` suffix existed only while `/app/estates` was the module's *overview* and the list
        needed an address of its own. Under `/app/development` those are already two addresses, and
        `/app/development/properties/all` now matches the detail route with an id of "all" — a back
        link that lands on an error state.
      */}
      <PageHeader
        title="Add a property"
        back={{ to: "/app/development/properties", label: "Properties" }}
        description="The scheme itself. Its units are added next, and a numbered range can be created in one step."
      />

      {/* The padlock where a plan refused, the ordinary red box for everything else. Adding a
          property is precisely the write a ceiling stops, and a red "no" there is a dead end. */}
      {save.errorMessage ? (
        <div className="mb-4">
          <Refusal error={save.error} message={save.errorMessage} />
        </div>
      ) : null}

      <Card>
        <form
          className="grid gap-4 sm:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault();
            const values = formValues(event.currentTarget);
            save.mutate({
              name: values.name,
              status: values.status,
              sells: values.sells,
              city: values.city || null,
              state: values.state || null,
              addressLine: values.addressLine || null,
              titleType: values.titleType || null,
              description: values.description || null,
              ...estateTermsPayload(terms),
            });
          }}
        >
          <div className="sm:col-span-2">
            <Field label="Name" htmlFor="name" required>
              <Input id="name" name="name" required placeholder="Palm Ridge Gardens" />
            </Field>
          </div>
          <Field label="Town or city" htmlFor="city">
            <Input id="city" name="city" placeholder="Ikeja" />
          </Field>
          <Field label="State" htmlFor="state">
            <Input id="state" name="state" placeholder="Lagos" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Address" htmlFor="addressLine">
              <Input id="addressLine" name="addressLine" placeholder="Off Airport Road" />
            </Field>
          </div>
          {/* The first thing a buyer asks about, so it is a field rather than a line in the notes. */}
          <Field label="Title" htmlFor="titleType" hint="The title under which the scheme is sold. For example, Certificate of Occupancy.">
            <Input id="titleType" name="titleType" placeholder="Certificate of Occupancy" />
          </Field>
          {/*
            What this scheme sells, which decides the words every later screen uses.

            Asked on the form that creates the property rather than buried in its settings,
            because it changes what the next screen calls things — and being asked "how many
            plots?" about a terrace of houses is the moment somebody decides this product is not
            for them. ADR-0081.
          */}
          <Field label="Type of scheme" htmlFor="sells" hint="Determines the terminology used throughout this property — plots, homes or units.">
            <Select id="sells" name="sells" value={sells} onChange={(event) => setSells(event.target.value)}>
              {SELLS.map((code) => (
                <option key={code} value={code}>
                  {SELLS_INFO[code].label} — {SELLS_INFO[code].hint}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Status" htmlFor="status">
            <Select id="status" name="status" defaultValue="selling">
              <option value="planning">Planning — not yet on sale</option>
              <option value="selling">On sale</option>
              <option value="sold_out">Fully sold</option>
              <option value="closed">Closed</option>
            </Select>
          </Field>
          <div className="sm:col-span-2">
            <Field label="Description" htmlFor="description" hint="Shown to buyers. For example, fully serviced plots with road, drainage and perimeter fencing.">
              <Input id="description" name="description" placeholder="Fully serviced plots with road, drainage and perimeter fencing." />
            </Field>
          </div>
          <div className="sm:col-span-2 rounded-[var(--radius-control)] border border-ink-200 p-3">
            <button
              type="button"
              className="text-sm font-medium text-brand-700 hover:underline"
              onClick={() => setReserving((open) => !open)}
            >
              {reserving ? "Hide how this property reserves" : "How this property reserves"}
            </button>
            <p className="mt-1 text-xs text-ink-500">
              Optional, and changeable at any time. These are the answers each reservation starts
              from; a reservation copies them and then owns its own.
            </p>
            {reserving ? (
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <ReservationTermsFields
                  draft={terms}
                  onChange={setTerms}
                  prefix="new-property-reservation"
                  noun={soldNoun(sells)}
                />
              </div>
            ) : null}
          </div>

          <div className="sm:col-span-2">
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Add the property"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
